import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtINR, fmtDate, today } from "@/lib/format";
import { ShieldCheck, BellRing } from "lucide-react";
import { STATUS_COLOR, INSURANCE_PLANS, INSURANCE_ADDONS } from "./_helpers";

export default function InsuranceTab() {
  const [rows, setRows] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [partners, setPartners] = useState([]);
  const [open, setOpen] = useState(false);
  const nextYear = new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().slice(0, 10);
  const emptyForm = { booking_id: "", insurer_id: "", insurer: "", plan_type: "comprehensive",
                       idv: 0, premium: 0, od_premium: 0, tp_premium: 0, ncb_pct: 0,
                       addons: [], start_date: today(), end_date: nextYear, remarks: "" };
  const [form, setForm] = useState(emptyForm);
  const [issueOpen, setIssueOpen] = useState(null);
  const [issueForm, setIssueForm] = useState({ insurer_policy_no: "" });

  const load = async () => {
    const [ins, b, p, rem] = await Promise.all([
      api.get("/dealership/insurance-policies"),
      api.get("/dealership/bookings"),
      api.get("/dealership/insurance-partners"),
      api.get("/dealership/insurance/renewal-reminders", { params: { days_ahead: 30 } }),
    ]);
    setRows(ins.data); setBookings(b.data); setPartners(p.data); setReminders(rem.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.booking_id || !form.insurer || form.idv <= 0) return toast.error("Booking, insurer and IDV are required");
    try {
      const r = await api.post("/dealership/insurance-policies", form);
      toast.success(`Policy created ${r.data.policy_number_local}`);
      setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const runIssue = async () => {
    if (!issueForm.insurer_policy_no) return toast.error("Insurer policy number is required");
    try {
      await api.post(`/dealership/insurance-policies/${issueOpen.id}/issue`, issueForm);
      toast.success(`Policy ${issueForm.insurer_policy_no} issued`);
      setIssueOpen(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const bookingLabel = (bid) => {
    if (!bid) return "— (no booking)";
    const b = bookings.find((x) => x.id === bid);
    return b ? `${b.booking_number} · ${b.customer?.first_name || ""}` : String(bid).slice(0, 8);
  };

  return (
    <div className="space-y-3" data-testid="insurance-tab">
      {reminders.length > 0 && (
        <div className="card-sharp p-4 border-amber-500/40 bg-amber-500/5" data-testid="renewal-reminders">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-amber-600 mb-2">
            <BellRing className="w-3.5 h-3.5" /> {reminders.length} policies expiring in ≤ 30 days
          </div>
          <div className="text-xs space-y-1">
            {reminders.slice(0, 5).map((p) => (
              <div key={p.id} className="flex items-center gap-3">
                <span className="font-mono">{p.policy_number_local}</span>
                <span className="text-muted-foreground">·</span>
                <span>{p.insurer}</span>
                <span className="text-muted-foreground">·</span>
                <span className="text-amber-600">expires {fmtDate(p.end_date)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex justify-end">
        <Button onClick={() => { setForm(emptyForm); setOpen(true); }} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="new-insurance-btn">
          <ShieldCheck className="w-3.5 h-3.5" /> New Insurance Policy
        </Button>
      </div>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Policy#</th><th>Booking</th><th>Insurer</th><th>Plan</th><th className="text-right">IDV</th><th className="text-right">Premium</th><th>End</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="9" className="text-center py-10 text-muted-foreground">No insurance policies yet.</td></tr>}
            {rows.map((p) => (
              <tr key={p.id} data-testid={`ins-row-${p.policy_number_local}`}>
                <td className="font-mono text-xs">{p.policy_number_local || p.policy_number || "—"}<br/><span className="text-[10px] text-muted-foreground">{p.insurer_policy_no || p.policy_number || "—"}</span></td>
                <td className="text-xs">{bookingLabel(p.booking_id)}</td>
                <td className="text-xs">{p.insurer || p.insurance_partner_name || "—"}</td>
                <td className="text-[10px] font-mono uppercase">{(p.plan_type || p.policy_type || "—").replace("_", " ")}</td>
                <td className="text-right tabular-nums text-xs">{fmtINR(p.idv || p.sum_insured || 0)}</td>
                <td className="text-right tabular-nums text-xs">{fmtINR(p.premium)}</td>
                <td className="text-xs">{fmtDate(p.end_date)}</td>
                <td><span className={`text-[10px] font-mono uppercase ${STATUS_COLOR[p.status] || ""}`}>{p.status}</span></td>
                <td className="text-right">
                  {p.status === "pending" && <Button size="sm" variant="ghost" className="h-7 text-xs text-emerald-600" onClick={() => { setIssueOpen(p); setIssueForm({ insurer_policy_no: "" }); }} data-testid={`issue-ins-${p.policy_number_local}`}>Issue</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-xl rounded-none space-y-3 overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading">New Insurance Policy</SheetTitle>
            <SheetDescription className="sr-only">Capture insurance plan, IDV and premium.</SheetDescription>
          </SheetHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Booking *</Label>
              <Select value={form.booking_id} onValueChange={(v) => setForm({ ...form, booking_id: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="ins-booking"><SelectValue placeholder="Pick booking" /></SelectTrigger>
                <SelectContent>{bookings.map((b) => <SelectItem key={b.id} value={b.id}>{b.booking_number} · {b.customer?.first_name} · {fmtINR(b.on_road_price)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Insurer *</Label>
              <Select value={form.insurer_id} onValueChange={(v) => {
                const p = partners.find((x) => x.id === v);
                setForm({ ...form, insurer_id: v, insurer: p?.name || "" });
              }}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="ins-partner"><SelectValue placeholder="Pick insurer" /></SelectTrigger>
                <SelectContent>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Plan type</Label>
              <Select value={form.plan_type} onValueChange={(v) => setForm({ ...form, plan_type: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="ins-plan"><SelectValue /></SelectTrigger>
                <SelectContent>{INSURANCE_PLANS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>IDV *</Label><Input type="number" value={form.idv} onChange={(e) => setForm({ ...form, idv: Number(e.target.value) })} className="rounded-sm mt-1" data-testid="ins-idv" /></div>
            <div><Label>Premium (gross)</Label><Input type="number" value={form.premium} onChange={(e) => setForm({ ...form, premium: Number(e.target.value) })} className="rounded-sm mt-1" data-testid="ins-premium" /></div>
            <div><Label>OD premium</Label><Input type="number" value={form.od_premium} onChange={(e) => setForm({ ...form, od_premium: Number(e.target.value) })} className="rounded-sm mt-1" /></div>
            <div><Label>TP premium</Label><Input type="number" value={form.tp_premium} onChange={(e) => setForm({ ...form, tp_premium: Number(e.target.value) })} className="rounded-sm mt-1" /></div>
            <div><Label>NCB %</Label><Input type="number" step="0.1" value={form.ncb_pct} onChange={(e) => setForm({ ...form, ncb_pct: Number(e.target.value) })} className="rounded-sm mt-1" /></div>
            <div><Label>Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-sm mt-1" /></div>
            <div><Label>End date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-sm mt-1" data-testid="ins-end" /></div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Add-ons</div>
            <div className="flex flex-wrap gap-2">
              {INSURANCE_ADDONS.map((a) => {
                const on = form.addons.includes(a);
                return (
                  <button key={a} type="button" data-testid={`addon-${a}`}
                          onClick={() => setForm({ ...form, addons: on ? form.addons.filter((x) => x !== a) : [...form.addons, a] })}
                          className={`text-[10px] font-mono uppercase px-2 py-1 border rounded-sm ${on ? "bg-accent text-white border-accent" : "bg-background"}`}>
                    {a.replace("_", " ")}
                  </button>
                );
              })}
            </div>
          </div>
          <Button onClick={save} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="ins-save">Create Policy</Button>
        </SheetContent>
      </Sheet>

      <Sheet open={!!issueOpen} onOpenChange={(v) => !v && setIssueOpen(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-3">
          <SheetHeader>
            <SheetTitle className="font-heading">Issue Policy</SheetTitle>
            <SheetDescription className="sr-only">Enter the insurer-issued policy number.</SheetDescription>
          </SheetHeader>
          {issueOpen && <>
            <div className="text-xs text-muted-foreground">Policy <span className="font-mono">{issueOpen.policy_number_local}</span> · {issueOpen.insurer}</div>
            <div><Label>Insurer policy number *</Label><Input value={issueForm.insurer_policy_no} onChange={(e) => setIssueForm({ insurer_policy_no: e.target.value })} className="rounded-sm mt-1 font-mono" data-testid="issue-policy-no" /></div>
            <Button onClick={runIssue} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="issue-run">Mark issued</Button>
          </>}
        </SheetContent>
      </Sheet>
    </div>
  );
}
